<template lang="pug">
	.variative-text-editor.flex-col.space-large
		.flex-col.space-large
			.flex-col.field
				.font-small.upper.grey {{$tCaption('Default text')}}
				DxTextBox(:value.sync="defaultText")
			Loader(v-if="isLoading", text="")
			template(v-else-if="services.length")
				.flex-row.space.align-center
					.font-big.upper.grey {{$tCaption('Text variations')}}
					DxButton(icon="plus", @click="addVariant")
				.flex-col.field(v-for="(variant, variantIdx) in variants")
					.flex-col.variant.space-large
						.flex-row.align-center
							.flex-col.flex-1
								.font-tiny.upper.grey {{$tCaption('Text')}}
								.flex-row.space.align-center
									.flex-1
										DxTextBox.used-services(:ref="`text_${variantIdx}`", :value.sync="variant.text", styling-mode="filled")
									UsedServicesContextMenu(target=".used-services", :servicesTextSource="stringify(variant.criteria)", :templateFields="templateFields", 
										:editingAreaResolver="() => $refs[`text_${variantIdx}`][0].$el.querySelector('input')", @change="v => variant.text = v", @menu-items-change="v => usedServices = v.all")
									DxButton(icon="trash", type="danger", stylingMode="text", @click="removeVariant(variantIdx)", :hint="$tCaption('Remove', '@actions')")
									DxButton(icon="copy", type="normal", stylingMode="text", @click="copyVariant(variant, variantIdx)", :hint="$tCaption('Clone', '@actions')")
						.flex-col
							.font-tiny.upper.grey {{$tCaption('Criteria')}}
							DxFilterBuilder(:ref="`variant_${variant.id}`", :customOperations="[]", :fields="criteriaFields" :value.sync="variant.criteria", :allow-hierarchical-fields="true", @value-changed="e => onFilterBuilderValueChanged(e, variant)")
								template(v-slot:output-template="{ data }")
									SSMLVariativeTextEditorHandler(@created="showEditorPopup(data, variant)")
							Popup(v-if="editorPopup.visible" :popupVisible="editorPopup.visible", :width="420", @hiding="applyEditingOutputValue()", :autosize="true")
								.flex-col.space-large.wrap
									.flex-col(v-if="editorPopup.output")
										.font-big.upper.grey {{$tCaption('Value')}}
										SSMLVariativeTextParamEditor(:param="editorPopup.output.param", :value.sync="editorPopup.value")
									.flex-col.space.parameters(@mousedown.prevent="")
										.flex-col.space-large
											StructureCriteria.structure-criteria-editor(:service="getService(editorPopup.data)", :hideCriteria="true", 
												:outputParamName="editorPopup && editorPopup.output && editorPopup.output.param && editorPopup.output.param.Name",
												:params="getServiceParams(editorPopup.data, editorPopup.variant)", :paramsCaptions="getServiceParams(editorPopup.data, editorPopup.variant, true)", :usedServices="usedServices",
												@change="(criteria, params, paramsCaptions, propagatedEditableParams) => onCriteriaChange(editorPopup.data, editorPopup.variant, criteria, params, paramsCaptions, propagatedEditableParams)",
												:associations="editingAssociations", @template-field-change="(param, templateFieldId) => onTemplateFieldAssociationChanged(templateFieldId, param, variant)"
												:allowTemplateFieldCreation="allowKeyEditing", @action="onCriteriaAction", :fullStructure="true")
								template(slot="actions-right")
									DxButton(:text="$tCaption('Close', '@actions')", @click="applyEditingOutputValue(false)")
									DxButton(:text="$tCaption('Save', '@actions')", @click="editorPopup.applyValue = true; applyEditingOutputValue()", type="success", :disabled="!editorPopup.value")
</template>

<script>
	import _ from 'lodash';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import DxFilterBuilder from 'devextreme-vue/filter-builder';
	import Loader from 'app-core/Loader.vue';
	import { EXTERNAL_SERVICES_CATEGORIES } from 'app-core/constants';
	import SSMLVariativeTextParamEditor from './SSMLVariativeTextParamEditor.vue';
	import StructureCriteria from 'view/webServiceCaller/StructureCriteria.vue';
	import Popup from 'app-core/Popup.vue';
	import SSMLVariativeTextEditorHandler from './SSMLVariativeTextEditorHandler.vue';
	import DxSelectBox from 'devextreme-vue/select-box';
	import UsedServicesContextMenu from './UsedServicesContextMenu.vue';
	import serviceHelper from 'view/webServiceCaller/service-helper';

	export default {
		translationCategory: 'insertion.SSMLVariativeTextEditor',
		name: 'variative-text-editor',
		components: {
			DxTextBox,
			DxButton,
			DxFilterBuilder,
			Loader,
			SSMLVariativeTextParamEditor,
			StructureCriteria,
			Popup,
			SSMLVariativeTextEditorHandler,
			DxSelectBox,
			UsedServicesContextMenu
		},
		model: {
			prop: 'value',
			event: 'change '
		},
		props: ['text', 'value', 'allowKeyEditing', 'templateFields'],
		data() {
			return {
				variants: this.getObject(this.value?.variants),
				defaultText: this.value?.defaultText,
				criteriaFields: [],
				isLoading: false,
				categories: EXTERNAL_SERVICES_CATEGORIES.toLocale('name', this),
				services: [],
				cachedInputValues: {},
				editorPopup: {
					visible: false,
					data: null,
					variant: null,
					output: null,
					value: null,
					initValue: null,
					v: null,
					applyValue: false,
					dataField: null
				},
				usedServices: []
			};
		},
		computed: {
			editingAssociations() {
				return this.editorPopup?.variant?.templateFieldsAssociation && this.editorPopup?.variant?.templateFieldsAssociation[this.editorPopup?.dataField];
			}
		},
		watch: {
			variants: {
				handler(v) {
					this.emitChange();
				},
				deep: true
			},
			defaultText() {
				this.emitChange();
			}
		},
		mounted() {
			if (__DEBUG__)
				window.variativeText = this;
			this.loadCriteriaFields();
		},
		methods: {
			onCriteriaAction(action, param) {
				switch(action) {
					case 'create-template-field': {
						if (__DEBUG__)
							console.debug('Create template field from param', { param });
						const templateField = serviceHelper.getTemplateFieldFromParam(param);
						this.$emit('create-template-field', templateField);
						break;
					}
				}
			},
			stringify(data) {
				return JSON.stringify(data);
			},
			onTemplateFieldAssociationChanged(value, param, variant) {
				if (!variant.templateFieldsAssociation) {
					this.$set(variant, 'templateFieldsAssociation',  {});
				}

				if (!variant.templateFieldsAssociation[this.editorPopup.data.field.dataField]) {
					this.$set(variant.templateFieldsAssociation, this.editorPopup.data.field.dataField, {});
				}

				this.$set(variant.templateFieldsAssociation[this.editorPopup.data.field.dataField], param.Name, value);
			},
			getTemplateFieldAssociation(variant, param) {
				return variant?.templateFieldsAssociation && variant.templateFieldsAssociation[this.editorPopup.data?.field?.dataField] && variant.templateFieldsAssociation[this.editorPopup.data.field.dataField][param?.Name];
			},
			showEditorPopup(data, variant) {
				this.editorPopup.data = data;
				this.editorPopup.dataField = data?.field?.dataField;
				this.editorPopup.variant = variant;
				this.editorPopup.visible = false;
				this.editorPopup.value = data.value || '';
				this.editorPopup.output = this.getOutput(data, variant);
				this.editorPopup.applyValue = false;
				setImmediate(() => {
					this.editorPopup.visible = true;
				});
			},
			emitChange() {
				this.$emit('change', {
					defaultText: this.defaultText,
					variants: this.variants && JSON.stringify(this.variants) || null
				});
			},
			copyVariant(variant, curIdx) {
				this.variants.splice(curIdx + 1, 0, {
					..._.cloneDeep(variant),
					id: this.$Data.uuid()
				});
			},
			onFilterBuilderValueChanged(e, variant) {
				if (__DEBUG__)
					console.debug('onFilterBuilderValueChanged', e, variant);
				this.fixRootElementsIfSelected(variant);
				this.removeUnusedVaraibles(variant);
			},
			fixRootElementsIfSelected(variant) {
				if (this.$modelFixing) return;
				this.$modelFixing = true;
				
				let builder = (this.$refs[`variant_${variant.id}`] || [])[0]?.instance;
				try {
					builder._model = serviceHelper.fixRootElements(builder._model, this.criteriaFields);
					variant.criteria = serviceHelper.fixRootElements(variant.criteria, this.criteriaFields);
				} finally {
					this.$nextTick(() => {
						this.$modelFixing = false;
						builder._refresh();
					});
				}
			},
			removeUnusedVaraibles(variant) {
				const json = JSON.stringify(variant.criteria);
				Object.keys(variant.params).map(f => {
					if (json.indexOf(f) === -1) {
						delete variant.params[f];
						delete variant.paramsCaptions[f];
					}
				})
			},
			applyEditingOutputValue() {
				const apply = this.editorPopup.applyValue;
				const value = `${(apply ? this.editorPopup.value : this.editorPopup.initValue) || ''}`
					.replace(/\[editingOutputValue\]/g, '');
				const criteria = [...((this.$refs[`variant_${this.editorPopup.variant.id}`] || [])[0]?.instance?._model || this.editorPopup.variant.criteria || [])];
				const textCriteria = JSON.stringify(criteria)
					.replace(/\[editingOutputValue\]/g, value);
				if (__DEBUG__)
					console.debug('applyEditingOutputValue', { value, textCriteria, oldCriteria: JSON.stringify(this.editorPopup.variant.criteria) });
				this.$set(this.editorPopup.variant, 'criteria', JSON.parse(textCriteria));
				this.editorPopup.visible = false;
			},
			getOutput(e, variant) {
				let service = this.services.find(f => e.field.dataField.indexOf(`service_${f.Oid}`) > -1);
				let serviceOutput = service.Outputs?.find(f => e.field.dataField.endsWith(`output_${f.Name}`));

				let value = e?.value !== void 0 ? e.value : (this.cachedInputValues[serviceOutput?.Name] || serviceOutput?.Value);
				let param = {
					id: e.field.dataField,
					caption: '',
					param: serviceOutput
				};

				this.editorPopup.value = (this.editorPopup.initValue = e.value || '')
					.replace(/\[editingOutputValue\]/g, '');

				e.setValue('[editingOutputValue]');

				Object.defineProperty(param, 'value', {
					get: () => value,
					set: (v) => {
						value = v;
						if (__DEBUG__)
							console.debug('Set value', { v, e });
						e.setValue(v);
						this.cachedInputValues[serviceOutput.Name] = v;
					}
				});

				if (serviceOutput?.DataType === 'boolean' && value === null) {
					setTimeout(() => {
						param.value = false;
					}, 200);
				}

				if (__DEBUG__)
					console.debug('getOutputs', { param, e, variant });

				return param;
			},
			getComplexParameters(e, variant) {
				let service = this.services.find(f => e.field.dataField.indexOf(`service_${f.Oid}`) > -1);
				return service.ComplexParameters;
			},
			getParameters(e, variant) {
				if (__DEBUG__)
					console.debug('getParameters', { e, variant });
				let service = this.services.find(f => e.field.dataField.indexOf(`service_${f.Oid}`) > -1);
				let inputParams = service.Parameters?.filter(f => f.UserEditable).map(f => {
					let inputParamId = `${e.field.dataField}.param_${f.Name}`;
					let obj = {
						id: inputParamId,
						caption: f.Name,
						param: f
					};
					
					if (!variant.params[inputParamId]) {
						variant.params[inputParamId] = this.cachedInputValues[f.Name] || f.Value;
						if (f?.DataType === 'boolean' && variant.params[inputParamId] === null)
							variant.params[inputParamId] = false;
					}

					Object.defineProperty(obj, 'value', {
						get: () => variant.params[inputParamId],
						set: (v) => {
							variant.params[inputParamId] = v;
							this.cachedInputValues[f.Name] = v;
						}
					})
					return obj;
				})

				if (__DEBUG__)
					console.debug('Get parameters', { service, inputParams });

				return inputParams;
			},
			getObject(v) {
				let obj = [];
				try {
					if (v)
						obj = JSON.parse(v);
				} catch(ex) {
					if (__DEBUG__)
						console.error(ex);
				}
				return obj;
			},
			addVariant() {
				this.variants.push({
					id: this.$Data.uuid(),
					text: '',
					criteria: null,
					params: {}
				});
			},
			loadCriteriaFields() {
				this.isLoading = true;
				this.$Data.getExternalServices(0, 999).then(async r => {
					let criteriaFields = [];
					this.services = [];
					r.Results.map(async f => f.JsonDefinition && this.services.push({ ...f.JsonDefinition, Oid: f.Oid, Name: f.Name, Category: f.Category, instance: f }));

					let categories = _.uniq(this.services.filter(f => f.Outputs?.length).map(f => f.Category));
					categories.map(f => {
						if (f > 0) {
							const categoryName = this.categories[f]?.name;
							criteriaFields.push({
								dataField: `category_${f}`,
								caption: categoryName,
								filterOperations: ['isnotblank']
							});
						}
					})

					const operations = {
						'string':	[ 'contains', 'notcontains', 'startswith', 'endswith', '=', '<>' ],
						'numeric':	[ '=', '<>', '<', '>', '<=', '>=' ],
						'number':	[ '=', '<>', '<', '>', '<=', '>=' ],
						'date':     [ '=', '<>', '<', '>', '<=', '>=' ],
						'datetime':	[ '=', '<>', '<', '>', '<=', '>=' ],
						'boolean':	[ '=', '<>' ],
						'object':	[ 'isblank', 'isnotblank' ],
						'list':     [ '=', '<>', ],
						'range':    [ '=', '<>', ]
					}

					this.services.filter(f => f.Outputs?.length).map(f => {
						let lastStruct = f.Category > 0 ? `category_${f.Category}` : '';

						lastStruct += `${lastStruct ? '.' : ''}service_${f.Oid}`;
						criteriaFields.push({
							dataField: lastStruct,
							caption: f.Name,
							filterOperations: ['isnotblank']
						});

						f.Outputs.map(p => {
							criteriaFields.push({
								dataField: `${lastStruct}.output_${p.Name}`,
								caption: p.Name,
								editorTemplate: 'output-template',
								param: p,
								dataType: p.DataType === 'list' || p.DataType === 'range' ? 'string' : p.DataType,
								filterOperations: operations[p.DataType],
								lookup: p.DataType === 'list' || p.DataType === 'range' ? {
									dataSource: p.ListOptions.map(f => ({ key: f.Key, value: f.Value || f.Key })),
									allowClearing: true,
									displayExpr: 'value',
									valueExpr: 'key'
								} : null
							});
						})
					});

					if (__DEBUG__)
						console.debug('Services loaded', { services: this.services, criteriaFields });

					this.criteriaFields = criteriaFields;
				}).finally(() => this.isLoading = false);
			},
			removeVariant(variantIdx) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to delete the current variant?'),
					handler: () => {
						this.variants.splice(variantIdx, 1);
					}
				});
			},
			onComplexParameterChange() {

			},
			getComplexParamValue() {

			},
			getService(e) {
				return this.services.find(f => e.field.dataField.indexOf(`service_${f.Oid}`) > -1)?.instance;
			},
			getServiceParams(e, variant, getCaptions) {
				const items = (getCaptions ? variant.paramsCaptions : variant.params) || [];
				const key = e.field.dataField;
				return items[key];
			},
			onCriteriaChange(e, variant, criteria, params, paramsCaptions, propagatedEditableParams) {
				if (__DEBUG__)
					console.debug('On criteria change', { e, variant, criteria, params, paramsCaptions, propagatedEditableParams });
				const allowedParams = Object.keys(params).filter(f => propagatedEditableParams && propagatedEditableParams[f]).reduce((res, item) => {
					res[item] = params[item];
					return res;
				}, {});
				let inputParamId = e.field.dataField;
				this.$set(variant.params, inputParamId, allowedParams);
				if (!variant.paramsCaptions) {
					variant.paramsCaptions = {};
				}
				if (paramsCaptions && Object.keys(paramsCaptions)) {
					this.$set(variant.paramsCaptions, inputParamId, paramsCaptions);
				}
			}
		}
	};
</script>
<style lang='scss'>
	.variative-text-editor {
		.variant {
			padding: 20px;
			border-radius: 4px;
			background: rgba(0, 0, 0, 0.02);
			border: 1px solid rgba(0,0,0,0.02);

			.dx-filterbuilder {
				background: none !important;
			}

			.parameters {
				padding: 10px 10px 16px 10px;
				border-radius: 4px;
				background: #fff;
				box-shadow: 2px 2px 8px 0px rgba(34, 60, 80, 0.2);
			}
		}

		.structure-criteria-editor {
			max-width: 400px;
		}
	}
</style>
